import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { mockCourses, mockUsers } from '../../data/mockData';

const CalendarView = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useApp();

  // Estados principales
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedShift, setSelectedShift] = useState('AM');
  const [generatedSchedule, setGeneratedSchedule] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [savedSchedules, setSavedSchedules] = useState({});
  const [showConflicts, setShowConflicts] = useState(false);
  const [conflicts, setConflicts] = useState([]);

  // Configuración de horarios
  const shifts = {
    AM: {
      name: 'Jornada Mañana',
      hours: ['08:00-08:45', '08:45-09:30', '09:45-10:30', '10:30-11:15', '11:30-12:15', '12:15-13:00'],
      description: '8:00 AM - 1:00 PM'
    },
    PM: {
      name: 'Jornada Tarde',
      hours: ['14:00-14:45', '14:45-15:30', '15:45-16:30', '16:30-17:15', '17:30-18:15', '18:15-19:00', '19:00-19:30'],
      description: '2:00 PM - 7:30 PM'
    },
    FULL: {
      name: 'Jornada Completa',
      hours: ['08:30-09:15', '09:15-10:00', '10:15-11:00', '11:00-11:45', '12:00-12:45', '14:30-15:15', '15:15-16:00', '16:15-17:00'],
      description: '8:30 AM - 5:00 PM'
    }
  };

  const days = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];

  // Cargar horarios guardados del localStorage
  useEffect(() => {
    const saved = localStorage.getItem('saved-schedules');
    if (saved) {
      setSavedSchedules(JSON.parse(saved));
    }
  }, []);

  // Algoritmo de generación automática de horarios
  const generateSchedule = async () => {
    if (!selectedCourse) {
      showError('Debe seleccionar un curso primero');
      return;
    }

    setIsGenerating(true);
    setConflicts([]);

    try {
      // Simular tiempo de procesamiento
      await new Promise(resolve => setTimeout(resolve, 1500));

      const course = mockCourses.find(c => c.id === selectedCourse);
      const schedule = await generateAutomaticSchedule(course, selectedShift);

      setGeneratedSchedule(schedule);

      // Verificar conflictos
      const detectedConflicts = detectConflicts(schedule, selectedShift);
      setConflicts(detectedConflicts);

      if (detectedConflicts.length > 0) {
        setShowConflicts(true);
        showError(`Se detectaron ${detectedConflicts.length} conflicto(s) de horario`);
      } else {
        showSuccess('Horario generado exitosamente sin conflictos');
      }

    } catch (error) {
      showError('Error al generar el horario');
    } finally {
      setIsGenerating(false);
    }
  };

  // Función para generar horario automáticamente
  const generateAutomaticSchedule = async (course, shift) => {
    const shiftHours = shifts[shift].hours;
    const totalSlots = days.length * shiftHours.length;
    const subjects = course.subjects || [];

    // Calcular total de horas requeridas
    const totalRequiredHours = subjects.reduce((sum, subject) => sum + subject.hours, 0);

    if (totalRequiredHours > totalSlots) {
      throw new Error('No hay suficientes slots para todas las materias');
    }

    // Crear matriz vacía del horario
    const schedule = {};
    days.forEach(day => {
      schedule[day] = new Array(shiftHours.length).fill(null);
    });

    // Distribuir materias por prioridad (más horas = más prioridad)
    const sortedSubjects = [...subjects].sort((a, b) => b.hours - a.hours);

    for (const subject of sortedSubjects) {
      let hoursAssigned = 0;
      let attempts = 0;
      const maxAttempts = 100;

      while (hoursAssigned < subject.hours && attempts < maxAttempts) {
        const randomDay = days[Math.floor(Math.random() * days.length)];
        const randomHour = Math.floor(Math.random() * shiftHours.length);

        if (!schedule[randomDay][randomHour]) {
          schedule[randomDay][randomHour] = {
            subject: subject.name,
            subjectId: subject.id,
            teacherId: subject.teacherId,
            teacherName: getTeacherName(subject.teacherId),
            color: getSubjectColor(subject.id)
          };
          hoursAssigned++;
        }
        attempts++;
      }

      if (hoursAssigned < subject.hours) {
        console.warn(`No se pudieron asignar todas las horas para ${subject.name}`);
      }
    }

    return schedule;
  };

  // Detectar conflictos de horarios entre profesores
  const detectConflicts = (schedule, shift) => {
    const conflicts = [];
    const teacherSchedule = {};

    // Construir horario de cada profesor
    Object.entries(schedule).forEach(([day, hours]) => {
      hours.forEach((slot, hourIndex) => {
        if (slot) {
          const teacherId = slot.teacherId;
          if (!teacherSchedule[teacherId]) {
            teacherSchedule[teacherId] = {};
          }
          if (!teacherSchedule[teacherId][day]) {
            teacherSchedule[teacherId][day] = {};
          }

          if (teacherSchedule[teacherId][day][hourIndex]) {
            // Conflicto detectado
            conflicts.push({
              teacher: slot.teacherName,
              day: day,
              hour: shifts[shift].hours[hourIndex],
              subjects: [
                teacherSchedule[teacherId][day][hourIndex].subject,
                slot.subject
              ],
              courses: [
                teacherSchedule[teacherId][day][hourIndex].course || selectedCourse,
                selectedCourse
              ]
            });
          } else {
            teacherSchedule[teacherId][day][hourIndex] = {
              ...slot,
              course: selectedCourse
            };
          }
        }
      });
    });

    return conflicts;
  };

  // Guardar horario
  const saveSchedule = () => {
    if (!generatedSchedule || !selectedCourse) return;

    const scheduleKey = `${selectedCourse}-${selectedShift}`;
    const newSavedSchedules = {
      ...savedSchedules,
      [scheduleKey]: {
        courseId: selectedCourse,
        courseName: mockCourses.find(c => c.id === selectedCourse)?.name,
        shift: selectedShift,
        schedule: generatedSchedule,
        savedAt: new Date().toISOString(),
        conflicts: conflicts.length
      }
    };

    setSavedSchedules(newSavedSchedules);
    localStorage.setItem('saved-schedules', JSON.stringify(newSavedSchedules));
    showSuccess('Horario guardado exitosamente');
  };

  // Cargar horario guardado
  const loadSchedule = (scheduleKey) => {
    const savedSchedule = savedSchedules[scheduleKey];
    if (savedSchedule) {
      setSelectedCourse(savedSchedule.courseId);
      setSelectedShift(savedSchedule.shift);
      setGeneratedSchedule(savedSchedule.schedule);
      setConflicts([]);
      showSuccess('Horario cargado exitosamente');
    }
  };

  // Funciones auxiliares
  const getTeacherName = (teacherId) => {
    const teacher = mockUsers.find(u => u.id === teacherId && u.role === 'profesor');
    return teacher?.name || 'Sin asignar';
  };

  const getSubjectColor = (subjectId) => {
    const colors = [
      '#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444',
      '#6366F1', '#EC4899', '#14B8A6', '#F97316', '#84CC16'
    ];
    return colors[subjectId % colors.length];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Generador de Horarios</h1>
          <p className="text-gray-600">Crea automáticamente la distribución de materias por curso</p>
        </div>
        <div className="flex space-x-3">
          {generatedSchedule && (
            <button
              onClick={saveSchedule}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span>Guardar Horario</span>
            </button>
          )}
        </div>
      </div>

      {/* Configuración */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Configuración</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Selección de Curso */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Curso
            </label>
            <select
              value={selectedCourse || ''}
              onChange={(e) => setSelectedCourse(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Seleccionar curso...</option>
              {mockCourses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.name} - {course.students} estudiantes
                </option>
              ))}
            </select>
          </div>

          {/* Selección de Jornada */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Jornada
            </label>
            <select
              value={selectedShift}
              onChange={(e) => setSelectedShift(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {Object.entries(shifts).map(([key, shift]) => (
                <option key={key} value={key}>
                  {shift.name} - {shift.description}
                </option>
              ))}
            </select>
          </div>

          {/* Botón Generar */}
          <div className="flex items-end">
            <button
              onClick={generateSchedule}
              disabled={!selectedCourse || isGenerating}
              className="w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
            >
              {isGenerating ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Generando...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span>Generar Horario</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Información del curso seleccionado */}
        {selectedCourse && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-md font-semibold text-gray-900 mb-2">
              Información del Curso
            </h3>
            {(() => {
              const course = mockCourses.find(c => c.id === selectedCourse);
              const totalHours = course?.subjects?.reduce((sum, s) => sum + s.hours, 0) || 0;
              const availableSlots = shifts[selectedShift].hours.length * days.length;

              return (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Curso:</span>
                    <div className="font-medium">{course?.name}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Estudiantes:</span>
                    <div className="font-medium">{course?.students}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Total horas:</span>
                    <div className={`font-medium ${totalHours > availableSlots ? 'text-red-600' : 'text-green-600'}`}>
                      {totalHours} / {availableSlots}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">Materias:</span>
                    <div className="font-medium">{course?.subjects?.length || 0}</div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Conflictos */}
      {conflicts.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-red-900">Conflictos Detectados</h2>
            <button
              onClick={() => setShowConflicts(!showConflicts)}
              className="text-red-700 hover:text-red-900 text-sm font-medium"
            >
              {showConflicts ? 'Ocultar' : 'Mostrar'} ({conflicts.length})
            </button>
          </div>

          {showConflicts && (
            <div className="space-y-2">
              {conflicts.map((conflict, index) => (
                <div key={index} className="bg-white border border-red-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2 text-sm">
                    <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <span className="font-medium text-red-900">
                      {conflict.teacher} - {conflict.day} {conflict.hour}
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-red-700 ml-6">
                    Conflicto entre: {conflict.subjects.join(' y ')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Horario Generado */}
      {generatedSchedule && (
        <ScheduleGrid
          schedule={generatedSchedule}
          shift={selectedShift}
          shifts={shifts}
          days={days}
        />
      )}

      {/* Horarios Guardados */}
      {Object.keys(savedSchedules).length > 0 && (
        <SavedSchedules
          savedSchedules={savedSchedules}
          onLoadSchedule={loadSchedule}
        />
      )}
    </div>
  );
};

// Componente para mostrar la grilla del horario
const ScheduleGrid = ({ schedule, shift, shifts, days }) => {
  const shiftHours = shifts[shift].hours;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        Horario Generado - {shifts[shift].name}
      </h2>

      <div className="overflow-x-auto">
        <div className="min-w-full">          <div className="grid grid-cols-6 gap-1">            {/* Header con horas */}            <div className="bg-gray-100 p-2 text-center font-semibold text-xs text-gray-700">              Hora            </div>            {days.map(day => (<div key={day} className="bg-gray-100 p-2 text-center font-semibold text-xs text-gray-700">                {day}              </div>))}                        {/* Filas por cada hora */}            {shiftHours.map((hour, hourIndex) => (<React.Fragment key={hour}>                <div className="bg-gray-50 p-2 text-center text-xs font-medium text-gray-600 border-r border-gray-200">                  {hour}                </div>                {days.map(day => { const slot = schedule[day]?.[hourIndex]; return (<div key={`${day}-${hourIndex}`} className={`p-2 text-center text-xs border border-gray-200 min-h-[60px] flex items-center justify-center ${slot ? 'text-white font-medium' : 'bg-gray-50'}`} style={{ backgroundColor: slot ? slot.color : undefined, opacity: slot ? 0.9 : 1 }}                    >                      {slot && (<div className="text-center">                          <div className="font-semibold text-xs leading-tight">                            {slot.subject}                          </div>                          <div className="text-xs opacity-90 mt-1">                            {slot.teacherName}                          </div>                        </div>)}                    </div>); })}              </React.Fragment>))}          </div>        </div>      </div>    </div>);
};// Componente para mostrar horarios guardados
// 

const SavedSchedules = ({ savedSchedules, onLoadSchedule }) => {  return (    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">      <h2 className="text-lg font-semibold text-gray-900 mb-4">Horarios Guardados</h2>            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">        {Object.entries(savedSchedules).map(([key, savedSchedule]) => (          <div key={key} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">            <div className="flex justify-between items-start mb-3">              <div>                <h3 className="font-semibold text-gray-900">{savedSchedule.courseName}</h3>                <p className="text-sm text-gray-600">{savedSchedule.shift === 'AM' ? 'Mañana' : savedSchedule.shift === 'PM' ? 'Tarde' : 'Completa'}</p>              </div>              {savedSchedule.conflicts > 0 && (                <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">                  {savedSchedule.conflicts} conflictos                </span>              )}            </div>                        <div className="text-xs text-gray-500 mb-3">              Guardado: {new Date(savedSchedule.savedAt).toLocaleDateString()}            </div>                        <button              onClick={() => onLoadSchedule(key)}              className="w-full bg-primary-500 hover:bg-primary-600 text-white text-sm py-2 px-3 rounded-lg font-medium transition-colors"            >              Cargar Horario            </button>          </div>        ))}      </div>    </div>  );};

export default CalendarView;